import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import {
  FaArrowLeft,
  FaInfoCircle,
  FaShieldAlt,
  FaUser,
  FaShip,
  FaAnchor,
  FaLifeRing,
  FaCalendarAlt,
  FaUsers,
  FaRulerCombined,
  FaTachometerAlt,
  FaClock,
  FaUtensils,
  FaWineGlassAlt,
  FaStar,
  FaRegStar,
  FaMapMarkerAlt,
  FaEuroSign,
  FaSwimmer,
  FaSun,
  FaSnowflake,
  FaCheck,
  FaChevronLeft,
  FaChevronRight,
  FaHeart,
  FaCompass,
  FaCalendarDay,
  FaMapMarkedAlt,
  FaUmbrellaBeach,
  FaWater
} from "react-icons/fa";
import { GiSailboat, GiWoodBeam, GiShipWheel, GiSpeedBoat } from "react-icons/gi";
import { BiDollar, BiSolidParty } from "react-icons/bi";
import { MdOutlineKitchen, MdSportsBar, MdLocationOn } from "react-icons/md";
import { IoWater, IoCalendarNumber } from "react-icons/io5";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Header from "../components/Header/Header";
import Footer from "../components/Footer/Footer";
import ImportantInfoModal from "../components/ImportantInfoModal/ImportantInfoModal";
import InsuranceModal from "../components/InsuranceModal/InsuranceModal";
import SuccessModal from "../components/SuccessModal/SuccessModal";
import LoadingSpinner from "../components/LoadingSpinner/LoadingSpinner";
import { yachts, yachtBookings, coupons, auth } from "../services/api";

const CustomDateInput = React.forwardRef(
  ({ value, onClick, placeholder, isError }, ref) => (
    <motion.button
      type="button"
      style={{
        border: isError ? "1px solid #ef4444" : "1px solid #e5e7eb",
        color: isError ? "#ef4444" : "#374151",
      }}
      className={`w-full h-[42px] px-3 rounded text-left bg-white hover:border-blue-500 transition-colors duration-300 ${
        isError ? "bg-red-50" : ""
      }`}
      onClick={onClick}
      ref={ref}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      {value || placeholder}
    </motion.button>
  )
);

const CustomInput = ({
  type,
  placeholder,
  value,
  onChange,
  className = "",
  ...props
}) => (
  <input
    type={type}
    placeholder={placeholder}
    value={value}
    onChange={onChange}
    className={`w-full h-[42px] px-3 border border-gray-200 rounded bg-white hover:border-blue-500 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 ${className}`}
    {...props}
  />
);

// Define color palette to ensure consistency
const colors = {
  primary: "#0B3D91", // Deep royal blue
  secondary: "#1565C0", // Medium blue
  accent: "#00B4D8", // Bright cyan
  highlight: "#4CC9F0", // Light blue
  warning: "#FFC107", // Amber yellow
  success: "#4CAF50", // Green
  error: "#F44336", // Red
  dark: "#1A1A2E", // Very dark blue
  light: "#FFFFFF", // White
  gray: "#F8FAFB", // Light gray
  textDark: "#333333", // Near black
  textLight: "#666666", // Gray
};

const YachtDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id: yachtId } = useParams();
  const { formData: initialFormData } = location.state || {};
  const [yacht, setYacht] = useState(null);
  const [showImportantInfo, setShowImportantInfo] = useState(false);
  const [showInsurance, setShowInsurance] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
    const [cruiseDuration, setCruiseDuration] = useState("half"); // "half" or "full"
  const [destinations, setDestinations] = useState({
    delos: false,
    rhenia: false,
    mykonos: false
  });

  const [formData, setFormData] = useState({
    bookingDate: initialFormData?.bookingDate
      ? new Date(initialFormData.bookingDate)
      : null,
    bookingTime: initialFormData?.bookingTime || "",
    cruiseType: initialFormData?.cruiseType || "",
    destination: initialFormData?.destination || "",
    numberOfGuests: initialFormData?.numberOfGuests || 1,
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    couponCode: initialFormData?.couponCode || "",
    notes: "",
  });

  const [errors, setErrors] = useState({});
  const [calculatedPrice, setCalculatedPrice] = useState(0);

  const [couponStatus, setCouponStatus] = useState({
    isValid: false,
    discount: 0,
    message: "",
  });

  useEffect(() => {
    const fetchYachtDetails = async () => {
      setIsLoading(true);
      try {
        const response = await yachts.getById(yachtId);
        setYacht(response.data);
      } catch (error) {
        console.error("Error fetching yacht details:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchYachtDetails();
  }, [yachtId, navigate]);

  const calculatePrice = useCallback(() => {
    if (!yacht || !formData.bookingDate || !formData.cruiseType) {
      return 0;
    }
    return calculatePriceManually();
  }, [yacht, formData.bookingDate, formData.cruiseType, formData.numberOfGuests, couponStatus]);
  // Enhance the manual price calculation to handle guest tiers
  const calculatePriceManually = () => {
    console.log("Using manual price calculation");
    
    if (!yacht?.pricing || !formData.bookingDate || !formData.cruiseType) {
      return 0;
    }

    // Determine cruise type category and duration type
    let cruiseCategory = "";  // "private" or "semiPrivate" 
    let durationType = "";    // "halfDay" or "fullDay"
    
    if (formData.cruiseType.toLowerCase().includes("semi")) {
      cruiseCategory = "semiPrivate";
    } else if (formData.cruiseType.toLowerCase().includes("private")) {
      cruiseCategory = "private";
    } else {
      console.warn("Unknown cruise type:", formData.cruiseType);
      return 0;
    }
    
    if (formData.cruiseType.toLowerCase().includes("half")) {
      durationType = "halfDay";
    } else if (formData.cruiseType.toLowerCase().includes("full")) {
      durationType = "fullDay";
    } else {
      console.warn("Unknown duration type:", formData.cruiseType);
      return 0;
    }
    
    // Determine season based on selected date
    const month = formData.bookingDate.getMonth(); // 0-based (0 = January, 11 = December)
    let seasonType = "";
    
    // April(3), May(4), September(8), October(9)
    if ([3, 4, 8, 9].includes(month)) {
      seasonType = "lowSeason";
    } 
    // June(5), July(6), August(7)
    else if ([5, 6, 7].includes(month)) {
      seasonType = "highSeason";
    }
    // All other months now use mediumSeason if available
    else {
      seasonType = "mediumSeason";
    }
    
    console.log(`Pricing lookup: ${cruiseCategory}.${durationType}.${seasonType}`);
    
    // Get pricing info from nested structure
    const seasonPricing = yacht.pricing?.[cruiseCategory]?.[durationType]?.[seasonType];
    
    if (!seasonPricing) {
      console.warn(`No pricing found for ${cruiseCategory}.${durationType}.${seasonType}`);
      return 0;
    }
    
    console.log("Season pricing:", seasonPricing);
    
    // Handle guest tier pricing
    if (seasonPricing.guestTiers && seasonPricing.guestTiers.length > 0) {
      console.log("Using guest tier pricing");
      
      // Find the appropriate tier for the number of guests
      const numGuests = formData.numberOfGuests || 1;
      let matchingTier = null;
      
      for (const tier of seasonPricing.guestTiers) {
        const guestRange = tier.guests.split('-').map(num => parseInt(num));
        
        if (guestRange.length === 2) {
          const [minGuests, maxGuests] = guestRange;
          if (numGuests >= minGuests && numGuests <= maxGuests) {
            matchingTier = tier;
            break;
          }
        } else if (guestRange.length === 1) {
          if (numGuests === guestRange[0]) {
            matchingTier = tier;
            break;
          }
        }
      }
      
      if (matchingTier) {
        console.log(`Found matching tier: ${matchingTier.guests} guests at rate ${matchingTier.rate}`);
        // Apply discount
        const discount = couponStatus.isValid ? couponStatus.discount : 0;
        const finalPrice = Math.max(0, matchingTier.rate - discount);
        console.log(`Final price after discount: ${finalPrice}`);
        return finalPrice;
      } else {
        // If no exact match, try to find the highest tier
        if (seasonPricing.guestTiers.length > 0) {
          const highestTier = seasonPricing.guestTiers[seasonPricing.guestTiers.length - 1];
          console.log(`No exact tier match. Using highest tier: ${highestTier.guests} at rate ${highestTier.rate}`);
          
          // Apply discount
          const discount = couponStatus.isValid ? couponStatus.discount : 0;
          const finalPrice = Math.max(0, highestTier.rate - discount);
          return finalPrice;
        }
      }
    }
    
    // Fall back to fixed rate or per guest rate if no tiers or no matching tier
    if (seasonPricing.fixedRate) {
      console.log(`Using fixed rate: ${seasonPricing.fixedRate}`);
      const discount = couponStatus.isValid ? couponStatus.discount : 0;
      return Math.max(0, seasonPricing.fixedRate - discount);
    } else if (seasonPricing.perGuestRate && formData.numberOfGuests > 0) {
      // Semi-private per guest calculation
      const totalPrice = seasonPricing.perGuestRate * formData.numberOfGuests;
      console.log(`Using per guest rate: ${seasonPricing.perGuestRate} × ${formData.numberOfGuests} = ${totalPrice}`);
      
      // Apply discount
      const discount = couponStatus.isValid ? couponStatus.discount : 0;
      const finalPrice = Math.max(0, totalPrice - discount);
      return finalPrice;
    }
    
    console.warn("No applicable pricing found");
    return 0;
  };

  // Update the price effect to log for debugging
  useEffect(() => {
    const price = calculatePrice();
    console.log(`Price calculated: ${price}`);
    setCalculatedPrice(price);
  }, [calculatePrice]);

  // Update the validateCoupon method to use the manual calculation
  const validateCoupon = async (code) => {
    if (!code || !yacht || !formData.bookingDate || !formData.cruiseType) {
      setCouponStatus({ isValid: false, discount: 0, message: "" });
      return;
    }

    let basePriceForValidation = calculatePriceManually();
    
    if (basePriceForValidation <= 0) {
      setCouponStatus({ isValid: false, discount: 0, message: "Cannot apply coupon with zero base price." });
      return;
    }

    try {
      const response = await coupons.validate(code, basePriceForValidation);
      setCouponStatus({
        isValid: true,
        discount: response.data.discount,
        message: "✓ Coupon applied successfully!",
      });
    } catch (error) {
      setCouponStatus({
        isValid: false,
        discount: 0,
        message: error.response?.data?.message || "✕ Invalid coupon code",
      });
    }
  };

  // Update the validateForm method to include validation for destinations
  const validateForm = () => {
    const newErrors = {};

    if (!formData.bookingDate) newErrors.bookingDate = "Booking date is required";
    if (!formData.bookingTime) newErrors.bookingTime = "Booking time is required";
    if (!formData.cruiseType) newErrors.cruiseType = "Cruise type is required";
    
    // Add validation for destinations
    if (formData.cruiseType && formData.cruiseType.toLowerCase().includes('half')) {
      const hasDestination = Object.values(destinations).some(val => val === true);
      if (!hasDestination) {
        newErrors.destinations = "Please select a destination for your half-day cruise";
      }
    }
    
    if (!formData.numberOfGuests || formData.numberOfGuests < 1) newErrors.numberOfGuests = "Number of guests must be at least 1";
    if (yacht && formData.numberOfGuests > yacht.guestCapacity) newErrors.numberOfGuests = `Maximum guests for this yacht is ${yacht.guestCapacity}`;

    if (!formData.firstName) newErrors.firstName = "First name is required";
    if (!formData.lastName) newErrors.lastName = "Last name is required";
    if (!formData.phone) newErrors.phone = "Phone number is required";

    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Invalid email address";
    }

    // Check if we have a valid price calculation
    if (calculatedPrice <= 0) {
      // Don't try to use yacht.calculatePriceAndAvailability
      newErrors.general = "Unable to proceed with booking. Please check your selections or try again later.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const [isPartner, setIsPartner] = useState(false);
  const [partnerId, setPartnerId] = useState(null);
  const [partnerCommission, setPartnerCommission] = useState(0);

  useEffect(() => {
    const checkPartnerStatus = async () => {
      const partnerToken = localStorage.getItem("partnerToken");
      if (partnerToken) {
        try {
          const response = await auth.getCurrentUser();
          if (response.data.user.isProfessionalPartner) {
            setIsPartner(true);
            setPartnerId(response.data.user._id);
            setPartnerCommission(response.data.user.commissionRate);
          }
        } catch (error) {
          console.error("Error checking partner status:", error);
        }
      }
    };
    checkPartnerStatus();
  }, []);

  // Update the handleSubmit method to not call calculatePriceAndAvailability directly
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      const firstError = document.querySelector('[data-error="true"]');
      if (firstError) {
        firstError.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      if (errors.general) {
        alert(`Booking Error: ${errors.general}`);
      }
      return;
    }

    if (!yacht) {
      alert("Yacht data is not loaded yet. Please wait or refresh.");
      return;
    }

    setIsSubmitting(true);

    // Don't try to call calculatePriceAndAvailability, just use our calculated price
    const finalCalculatedPrice = calculatedPrice;
    const finalDiscountAmount = couponStatus.isValid ? couponStatus.discount : 0;

    const bookingData = {
      yachtId: yacht._id,
      customer: {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phoneNumber: formData.phone,
      },
      bookingDate: formData.bookingDate.toISOString(),
      bookingTime: formData.bookingTime,
      cruiseType: formData.cruiseType,
      destination: formData.destination,
      numberOfGuests: parseInt(formData.numberOfGuests, 10),
      couponCode: couponStatus.isValid ? formData.couponCode : undefined,
      additionalNotes: formData.notes,
      professionalPartnerId: isPartner ? partnerId : undefined,
    };

    // Include destinations in form submission
    const selectedDestinations = Object.entries(destinations)
      .filter(([_, selected]) => selected)
      .map(([destination, _]) => destination);
      
    try {
      // Add destinations to the form data
      bookingData.destination = selectedDestinations.length > 0 ? selectedDestinations : undefined;

      // The server will perform its own price calculation when creating the booking
      const response = await yachtBookings.create(bookingData);
      setShowSuccess(true);
    } catch (error) {
      console.error("Error submitting yacht booking:", error);
      alert(
        `Failed to submit booking request: ${
          error.response?.data?.message || error.message
        }. Please try again.`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // New state variables for enhanced UI
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [isAnimating, setIsAnimating] = useState(false);
  const [isFavorited, setIsFavorited] = useState(false);
  
  const carouselRef = useRef(null);
  const { scrollYProgress } = useScroll();
  const headerOpacity = useTransform(scrollYProgress, [0, 0.1], [1, 0.8]);
  
  // Auto rotate carousel every few seconds
  useEffect(() => {
    if (!yacht || !yacht.images || yacht.images.length <= 1) return;
    
    const timer = setInterval(() => {
      setActiveImageIndex((prevIndex) => 
        prevIndex === yacht.images.length - 1 ? 0 : prevIndex + 1
      );
    }, 4000); // Change image every 4 seconds
    
    return () => clearInterval(timer);
  }, [yacht]);

  // Handle changing carousel image
  const changeImage = (index) => {
    setIsAnimating(true);
    setActiveImageIndex(index);
    setTimeout(() => setIsAnimating(false), 500);
  };

  // Open full gallery modal
  const openGallery = (startIndex) => {
    setActiveImageIndex(startIndex);
    setShowGalleryModal(true);
    document.body.style.overflow = 'hidden'; // Prevent background scrolling
  };

  // Close gallery modal
  const closeGallery = () => {
    setShowGalleryModal(false);
    document.body.style.overflow = 'auto'; // Re-enable scrolling
  };

  // Toggle favorite status of yacht
  const toggleFavorite = () => {
    setIsFavorited(!isFavorited);
    // TODO: Add functionality to save favorite to localStorage
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        duration: 0.5,
        when: "beforeChildren",
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { duration: 0.5 }
    }
  };

  const fadeInVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { duration: 0.5 }
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="xl" />
      </div>
    );
  }

  if (!yacht) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
         <Header />
         <main className="container mx-auto px-4 py-8 text-center">
            <h1 className="text-2xl font-bold text-red-600 mb-4">Yacht Not Found</h1>
            <p className="text-gray-600 mb-6">The yacht you are looking for could not be loaded or does not exist.</p>
            <button
                onClick={() => navigate('/yachts')}
                className="px-6 py-2 bg-blue-900 text-white rounded hover:bg-blue-700 transition-colors"
            >
                Back to Yachts
            </button>
         </main>
         <Footer />
      </div>
    );
  }

  // Format price for display
  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  // Get an array of available cruise types
  const cruiseTypes = yacht.cruiseOptions ? 
    yacht.cruiseOptions.map(option => option.type) : [];

  // Extract all pricing information
  const pricingByType = {
    private: {
      halfDay: yacht.pricing?.private?.halfDay || {},
      fullDay: yacht.pricing?.private?.fullDay || {}
    },
    semiPrivate: {
      halfDay: yacht.pricing?.semiPrivate?.halfDay || {},
      fullDay: yacht.pricing?.semiPrivate?.fullDay || {}
    }
  };

  console.log("Pricing by type:", pricingByType);

  // Get seasons information
  const seasons = yacht.pricing?.seasonDefinition || {
    low: "Low season",
    high: "High season"
  };

  // Handle cruise duration change
  const handleDurationChange = (e) => {
    const newDuration = e.target.value;
    setCruiseDuration(newDuration);
    
    // If full day selected, check all destinations
    if (newDuration === "full") {
      setDestinations({
        delos: true,
        rhenia: true,
        mykonos: true
      });
    } else {
      // If half day, reset destinations
      setDestinations({
        delos: false,
        rhenia: false,
        mykonos: false
      });
    }
  };

  // Handle destination checkbox changes
  const handleDestinationChange = (e) => {
    const { name, checked } = e.target;
    
    // If it's a half day, uncheck other destinations when one is selected
    if (cruiseDuration === "half" && checked) {
      const newDestinations = {
        delos: false,
        rhenia: false,
        mykonos: false
      };
      newDestinations[name] = checked;
      setDestinations(newDestinations);
    } else {
      setDestinations({
        ...destinations,
        [name]: checked
      });
    }
  };

  return (
    <>
      {/* Main Container */}
      <div className="min-h-screen bg-gray-50 overflow-x-hidden">
        {/* Processing Overlay - existing code */}
        {isSubmitting && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
            <div className="bg-white p-8 rounded-lg shadow-lg text-center">
              <LoadingSpinner size="lg" />
              <p className="mt-4 text-lg font-semibold text-gray-700">
                Processing your booking...
              </p>
              <p className="mt-2 text-sm text-gray-500">
                Please don't close this window
              </p>
            </div>
          </div>
        )}

        <Header />

        {/* Hero Section with Carousel */}
        <section className="relative h-[60vh] md:h-[70vh] overflow-hidden">
          <motion.div 
            className="absolute inset-0 z-10 bg-gradient-to-b from-black/60 to-transparent"
            style={{ opacity: headerOpacity }}
          >
            <div className="container mx-auto px-4 pt-8 relative z-10">
              <motion.button
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                onClick={() => navigate(-1)}
                className="flex items-center text-white hover:text-highlight transition-colors"
              >
                <FaArrowLeft className="mr-2" />
                <span>Back to yachts</span>
              </motion.button>
              
              <div className="mt-12 flex flex-col items-start">
                <motion.div 
                  initial={{ y: -20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  className="flex items-center"
                >
                  <span className="px-3 py-1 rounded-full bg-white/20 backdrop-blur-sm text-white text-sm">
                    {yacht.yachtType}
                  </span>
                  <motion.button 
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={toggleFavorite}
                    className="ml-3 p-2 rounded-full bg-white/20 backdrop-blur-sm text-white"
                  >
                    <FaHeart className={isFavorited ? "text-red-500" : "text-white/70"} />
                  </motion.button>
                </motion.div>
                
                <motion.h1 
                  initial={{ y: -20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mt-4 drop-shadow-lg"
                >
                  {yacht.builder} {yacht.name}
                </motion.h1>
                
                <motion.div 
                  initial={{ y: -20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  className="flex items-center mt-2"
                >
                  <GiSailboat className="text-white mr-2" />
                  <span className="text-white/90">{yacht.builder}</span>
                  {yacht.yearBuilt && (
                    <>
                      <span className="mx-2 text-white/70">•</span>
                      <FaCalendarAlt className="text-white mr-2" />
                      <span className="text-white/90">Built {yacht.yearBuilt}</span>
                    </>
                  )}
                </motion.div>

                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.5 }}
                  className="flex flex-wrap gap-4 mt-8"
                >
                  <div className="px-4 py-2 bg-white/20 backdrop-blur-sm rounded-lg flex items-center">
                    <FaUsers className="text-accent mr-2" />
                    <div>
                      <div className="text-white text-lg font-bold">{yacht.guestCapacity}</div>
                      <div className="text-white/80 text-xs">Guests</div>
                    </div>
                  </div>
                  
                  <div className="px-4 py-2 bg-white/20 backdrop-blur-sm rounded-lg flex items-center">
                    <FaRulerCombined className="text-accent mr-2" />
                    <div>
                      <div className="text-white text-lg font-bold">{yacht.specifications?.length}</div>
                      <div className="text-white/80 text-xs">Length</div>
                    </div>
                  </div>

                  <div className="px-4 py-2 bg-white/20 backdrop-blur-sm rounded-lg flex items-center">
                    <GiWoodBeam className="text-accent mr-2" />
                    <div>
                      <div className="text-white text-lg font-bold">{yacht.specifications?.beam}</div>
                      <div className="text-white/80 text-xs">Beam</div>
                    </div>
                  </div>

                  {yacht.crewCapacity > 0 && (
                    <div className="px-4 py-2 bg-white/20 backdrop-blur-sm rounded-lg flex items-center">
                      <FaLifeRing className="text-accent mr-2" />
                      <div>
                        <div className="text-white text-lg font-bold">{yacht.crewCapacity}</div>
                        <div className="text-white/80 text-xs">Crew</div>
                      </div>
                    </div>
                  )}
                </motion.div>
              </div>
            </div>
          </motion.div>

          {/* Image Carousel */}
          <div className="absolute inset-0">
            <AnimatePresence mode="wait">
              {yacht.images && yacht.images.length > 0 && (
                <motion.img
                  key={activeImageIndex}
                  src={`${'https://yacht-server.create-site.gr'}${yacht.images[activeImageIndex]}`}
                  alt={`${yacht.name} - View ${activeImageIndex + 1}`}
                  className="w-full h-full object-cover object-center"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5 }}
                  onClick={() => openGallery(activeImageIndex)}
                />
              )}
            </AnimatePresence>
            
            {/* Carousel Navigation */}
            {yacht.images && yacht.images.length > 1 && (
              <div className="absolute bottom-10 left-0 right-0 flex justify-center gap-2 z-20">
                {yacht.images.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => changeImage(index)}
                    className={`w-3 h-3 rounded-full transition-all ${
                      index === activeImageIndex ? 'bg-white scale-110' : 'bg-white/50'
                    }`}
                    aria-label={`View image ${index + 1}`}
                  />
                ))}
              </div>
            )}
            
            {/* Gallery Controls */}
            {yacht.images && yacht.images.length > 1 && (
              <>
                <button 
                  onClick={() => changeImage(activeImageIndex === 0 ? yacht.images.length - 1 : activeImageIndex - 1)}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 p-3 rounded-full bg-black/30 text-white hover:bg-black/50 transition-all z-20"
                  disabled={isAnimating}
                >
                  <FaChevronLeft />
                </button>
                <button 
                  onClick={() => changeImage(activeImageIndex === yacht.images.length - 1 ? 0 : activeImageIndex + 1)}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 p-3 rounded-full bg-black/30 text-white hover:bg-black/50 transition-all z-20"
                  disabled={isAnimating}
                >
                  <FaChevronRight />
                </button>
              </>
            )}
            
            {/* View Gallery Button */}
            <button 
              onClick={() => openGallery(0)}
              className="absolute bottom-10 right-10 bg-white/20 backdrop-blur-sm hover:bg-white/30 transition-all text-white px-4 py-2 rounded-lg z-20"
            >
              View All Photos
            </button>
          </div>
        </section>

        <main className="container mx-auto px-4 py-8 -mt-10 relative z-20 JosefinSans bg-[#F5F3EF]">
            <div className="flex flex-col lg:flex-row gap-8 h-full">
                {/* Left Column - Enhanced Yacht Details */}
                <div className="lg:w-2/3 flex flex-col">
                  {/* Tabs Navigation */}
                  <div className="bg-gradient-to-r from-[#002B5B] to-[#00AEEF] text-white px-4 rounded-t-lg shadow-md flex overflow-x-auto hide-scrollbar">
                    {['overview', 'specifications', 'pricing', 'amenities'].map(tab => (
                      <button
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        className={`px-6 py-4 font-medium text-sm transition-colors whitespace-nowrap ${
                          activeTab === tab 
                            ? 'text-white border-b-2 border-[#7FDBB6]' 
                            : 'text-white/80 hover:text-[#7FDBB6]'
                        }`}
                      >
                        {tab.charAt(0).toUpperCase() + tab.slice(1)}
                      </button>
                    ))}
                  </div>
                  
                  {/* Content Sections */}
                  <div className="bg-white p-6 rounded-b-lg shadow-md mb-8 flex-1 border-t-0 border-[#E5E5E5]">
                    <AnimatePresence mode="wait">
                      {/* Overview Tab */}
                      {activeTab === 'overview' && (
                        <motion.div 
                          key="overview"
                          initial="hidden"
                          animate="visible"
                          exit="hidden"
                          variants={containerVariants}
                          className="space-y-8"
                        >
                          <motion.div variants={itemVariants}>
                            <h2 className="text-2xl font-bold text-[#002B5B] mb-4 flex items-center">
                              <FaInfoCircle className="mr-2 text-[#7FDBB6]" /> About {yacht.builder} {yacht.name}

                            </h2>
                            <div className="prose max-w-none text-[#2E3A59]">
                              <p>{yacht.description || "No description available."}</p>
                            </div>
                          </motion.div>

                          <motion.div variants={itemVariants}>
                            <h3 className="text-xl font-semibold text-[#002B5B] mb-3">Key Features</h3>
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                              <div className="bg-[#F5F3EF] p-4 rounded-lg text-center">
                                <FaShip className="text-[#7FDBB6] text-2xl mx-auto mb-2" />
                                <p className="font-medium text-[#2E3A59]">{yacht.yachtType}</p>
                                <p className="text-sm text-[#7A8B99]">Type</p>
                              </div>
                              
                              <div className="bg-[#F5F3EF] p-4 rounded-lg text-center">
                                <FaCalendarAlt className="text-[#7FDBB6] text-2xl mx-auto mb-2" />
                                <p className="font-medium text-[#2E3A59]">{yacht.yearBuilt}</p>
                                <p className="text-sm text-[#7A8B99]">Year Built</p>
                              </div>
                              
                              <div className="bg-[#F5F3EF] p-4 rounded-lg text-center">
                                <FaUsers className="text-[#7FDBB6] text-2xl mx-auto mb-2" />
                                <p className="font-medium text-[#2E3A59]">{yacht.guestCapacity}</p>
                                <p className="text-sm text-[#7A8B99]">Max Guests</p>
                              </div>
                              
                              <div className="bg-[#F5F3EF] p-4 rounded-lg text-center">
                                <FaLifeRing className="text-[#7FDBB6] text-2xl mx-auto mb-2" />
                                <p className="font-medium text-[#2E3A59]">{yacht.crewCapacity}</p>
                                <p className="text-sm text-[#7A8B99]">Crew</p>
                              </div>
                            </div>
                          </motion.div>                          <motion.div variants={itemVariants}>
                            <h3 className="text-xl font-semibold text-[#002B5B] mb-3">Choose Your Experience</h3>
                            <p className="mb-3 text-[#2E3A59]">Cruise in style aboard our {yacht.builder} {yacht.name} & select your journey!</p>
                            
                            <div className="space-y-6">
                              {/* Private Cruise Options */}
                              <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm">
                                <div className="flex items-center mb-3">
                                  <FaShip className="mr-2 text-[#002B5B]" />
                                  <h4 className="font-semibold text-lg text-[#002B5B]">Private Mykonos Cruises</h4>
                                </div>
                                <p className="text-[#2E3A59] mb-3">
                                  Enjoy an exclusive, fully personalized experience just for you and your group.
                                </p>
                                <div className="mb-3">
                                  <p className="font-medium mb-2">Choose Your Perfect time slot:</p>
                                  <ul className="space-y-2 text-[#2E3A59]">
                                    <li className="flex items-center">
                                      <FaClock className="text-accent mr-2 flex-shrink-0" /> 
                                      <span>Half-Day Morning Cruise | 10am - 3pm</span>
                                    </li>
                                    <li className="flex items-center">
                                      <FaSun className="text-accent mr-2 flex-shrink-0" /> 
                                      <span>Half-Day Sunset Cruise | 3:30pm - 8:30pm</span>
                                    </li>
                                    <li className="flex items-center">
                                      <FaCalendarDay className="text-accent mr-2 flex-shrink-0" /> 
                                      <span>Full-Day Cruise | Any 8-hour period between 10:00am & Sunset</span>
                                    </li>
                                  </ul>
                                </div>
                                <p className="text-sm italic text-[#7A8B99]">
                                  Note: Time slots may change during September & October due to sunset times.
                                </p>
                              </div>
                              
                              {/* Semi-Private Cruise Options */}
                              <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm">
                                <div className="flex items-center mb-3">
                                  <FaUsers className="mr-2 text-[#002B5B]" />
                                  <h4 className="font-semibold text-lg text-[#002B5B]">Semi-Private Mykonos Cruises</h4>
                                </div>
                                <p className="text-[#2E3A59] mb-3">
                                  Join a small group of travelers for a fun, relaxed sailing adventure.
                                </p>
                                <div className="mb-3">
                                  <p className="font-medium mb-2">Choose Your Perfect time slot:</p>
                                  <ul className="space-y-2 text-[#2E3A59]">
                                    <li className="flex items-center">
                                      <FaClock className="text-accent mr-2 flex-shrink-0" /> 
                                      <span>Half-Day Morning Cruise | 10am - 3pm</span>
                                    </li>
                                    <li className="flex items-center">
                                      <FaSun className="text-accent mr-2 flex-shrink-0" /> 
                                      <span>Half-Day Sunset Cruise | 3:30pm - 8:30pm</span>
                                    </li>
                                  </ul>
                                </div>
                                <p className="text-sm italic text-[#7A8B99]">
                                  Note: Time slots may change during September & October due to sunset times.
                                </p>
                              </div>
                              
                              {/* Top Destinations */}
                              <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm">
                                <div className="flex items-center mb-3">
                                  <FaMapMarkedAlt className="mr-2 text-[#002B5B]" />
                                  <h4 className="font-semibold text-lg text-[#002B5B]">Top Mykonos Destinations to Explore</h4>
                                </div>
                                <ul className="space-y-3 mb-4 text-[#2E3A59]">
                                  <li className="flex items-start">
                                    <FaCompass className="text-accent mr-2 mt-1 flex-shrink-0" /> 
                                    <div>
                                      <span className="font-medium">Delos Island</span> - Discover ancient ruins and mythological wonders on this UNESCO World Heritage site.
                                    </div>
                                  </li>
                                  <li className="flex items-start">
                                    <FaUmbrellaBeach className="text-accent mr-2 mt-1 flex-shrink-0" /> 
                                    <div>
                                      <span className="font-medium">Rhenia Island</span> - Escape to pristine beaches and crystal-clear waters, perfect for a relaxing getaway.
                                    </div>
                                  </li>
                                  <li className="flex items-start">
                                    <FaWater className="text-accent mr-2 mt-1 flex-shrink-0" /> 
                                    <div>
                                      <span className="font-medium">Mykonos Island</span> - Uncover the beauty of Mykonos with its picturesque South Coastline and famous beaches.
                                    </div>
                                  </li>
                                </ul>
                                <p className="text-[#2E3A59]">
                                  Choose one destination for a Half-Day Private or Semi-Private Cruise.
                                  For the ultimate adventure, combine all destinations on a Private Full-Day Cruise!
                                </p>
                                <p className="text-sm italic text-[#7A8B99] mt-2">
                                  Note: Itinerary is always subject to weather conditions.
                                </p>
                              </div>
                              
                              {/* Available Cruise Options */}
                                              <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm">
                                              <h4 className="font-medium text-lg mb-3 text-[#002B5B]">Available Cruise Options</h4>
                                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                {yacht.cruiseOptions?.map((option, index) => {
                                                // Skip rendering for Semi-Private Full Day option
                                                if (option.type.toLowerCase().includes("semi") && 
                                                  option.type.toLowerCase().includes("full")) {
                                                  return null;
                                                }
                                                
                                                return (
                                                  <div 
                                                  key={index} 
                                                  className="border border-gray-200 rounded-lg p-4 transition-all hover:shadow-md"
                                                  >
                                                  <div className="flex items-start justify-between">
                                                    <div>
                                                    <h4 className="font-medium">{option.type}</h4>
                                                    <p className="text-sm text-[#7A8B99] flex items-center mt-1">
                                                      <FaClock className="mr-1 text-accent" /> {option.durationHours} hours
                                                    </p>
                                                    </div>
                                                    <div className="bg-green-100 text-green-700 px-2 py-1 rounded text-sm font-medium">
                                                    Available
                                                    </div>
                                                  </div>
                                                  </div>
                                                );
                                                })}
                                              </div>
                                              </div>
                                            </div>
                                            </motion.div>

                                            {/* Quick Price Overview */}
                          {yacht.lowestPriceInfo && (
                            <motion.div variants={itemVariants} className="bg-[#002B5B]/5 p-5 rounded-lg border border-[#002B5B]/20">
                              <h3 className="text-xl font-semibold text-[#002B5B] mb-2 flex items-center">
                                <FaEuroSign className="mr-2" /> Pricing
                              </h3>
                              <p className="text-[#2E3A59] mb-2">
                                Prices start from <span className="font-bold text-[#002B5B] text-lg">
                                  {formatPrice(yacht.lowestPriceInfo.price)}
                                </span> for {yacht.lowestPriceInfo.type}
                              </p>
                              <p className="text-sm text-[#7A8B99]">
                                * Prices vary by season and cruise type. See Pricing tab for details.
                              </p>
                            </motion.div>
                          )}

                          {/* Image Gallery Preview */}
                          {yacht.images && yacht.images.length > 1 && (
                            <motion.div variants={itemVariants}>
                              <h3 className="text-xl font-semibold text-[#002B5B] mb-3 flex items-center">
                                <FaShip className="mr-2 text-[#7FDBB6]" /> Gallery
                              </h3>
                              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                {yacht.images.slice(0, 6).map((img, index) => (
                                  <div 
                                    key={index} 
                                    className="relative aspect-square overflow-hidden rounded-lg cursor-pointer"
                                    onClick={() => openGallery(index)}
                                  >
                                    <img 
                                      src={`${'https://yacht-server.create-site.gr'}${img}`}
                                      alt={`${yacht.name} view ${index + 1}`}
                                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                                    />
                                  </div>
                                ))}
                                {yacht.images.length > 6 && (
                                  <div 
                                    className="relative aspect-square overflow-hidden rounded-lg bg-primary cursor-pointer flex items-center justify-center text-white font-bold text-xl"
                                    onClick={() => openGallery(6)}
                                  >
                                    +{yacht.images.length - 6} more
                                  </div>
                                )}
                              </div>
                            </motion.div>
                          )}
                        </motion.div>
                      )}

                      {/* Specifications Tab */}
                      {activeTab === 'specifications' && (
                        <motion.div 
                          key="specifications"
                          initial="hidden"
                          animate="visible"
                          exit="hidden"
                          variants={containerVariants}
                          className="space-y-8"
                        >
                          <motion.div variants={itemVariants}>
                            <h2 className="text-2xl font-bold text-[#002B5B] mb-4 flex items-center">
                              <FaRulerCombined className="mr-2 text-[#7FDBB6]" /> Yacht Specifications
                            </h2>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                              {/* Core Specifications */}
                              <div className="bg-gray p-6 rounded-lg">
                                <h3 className="text-lg font-semibold mb-4 border-b pb-2">Dimensions & Capacity</h3>
                                <ul className="space-y-4">
                                  <li className="flex items-center justify-between">
                                    <span className="flex items-center text-textLight">
                                      <FaRulerCombined className="mr-2 text-accent" /> Length
                                    </span>
                                    <span className="font-semibold">{yacht.specifications?.length || '-'}</span>
                                  </li>
                                  <li className="flex items-center justify-between">
                                    <span className="flex items-center text-textLight">
                                      <GiWoodBeam className="mr-2 text-accent" /> Beam
                                    </span>
                                    <span className="font-semibold">{yacht.specifications?.beam || '-'}</span>
                                  </li>
                                  <li className="flex items-center justify-between">
                                    <span className="flex items-center text-textLight">
                                      <FaAnchor className="mr-2 text-accent" /> Draft
                                    </span>
                                    <span className="font-semibold">{yacht.specifications?.draft || '-'}</span>
                                  </li>
                                  <li className="flex items-center justify-between">
                                    <span className="flex items-center text-textLight">
                                      <FaUsers className="mr-2 text-accent" /> Guest Capacity
                                    </span>
                                    <span className="font-semibold">{yacht.guestCapacity || '-'}</span>
                                  </li>
                                  <li className="flex items-center justify-between">
                                    <span className="flex items-center text-textLight">
                                      <FaLifeRing className="mr-2 text-accent" /> Crew
                                    </span>
                                    <span className="font-semibold">{yacht.crewCapacity || '-'}</span>
                                  </li>
                                  <li className="flex items-center justify-between">
                                    <span className="flex items-center text-textLight">
                                      <GiShipWheel className="mr-2 text-accent" /> Yacht Type
                                    </span>
                                    <span className="font-semibold">{yacht.yachtType || '-'}</span>
                                  </li>
                                </ul>
                              </div>

                              {/* Additional Information */}
                              <div className="bg-gray p-6 rounded-lg">
                                <h3 className="text-lg font-semibold mb-4 border-b pb-2">Additional Information</h3>
                                <ul className="space-y-4">
                                  <li className="flex items-center justify-between">
                                    <span className="flex items-center text-textLight">
                                      <FaCalendarAlt className="mr-2 text-accent" /> Year Built
                                    </span>
                                    <span className="font-semibold">{yacht.yearBuilt || '-'}</span>
                                  </li>
                                  <li className="flex items-center justify-between">
                                    <span className="flex items-center text-textLight">
                                      <IoCalendarNumber className="mr-2 text-accent" /> Year Refitted
                                    </span>
                                    <span className="font-semibold">{yacht.yearRefitted || '-'}</span>
                                  </li>
                                  <li className="flex items-center justify-between">
                                    <span className="flex items-center text-textLight">
                                      <FaShip className="mr-2 text-accent" /> Builder
                                    </span>
                                    <span className="font-semibold">{yacht.builder || '-'}</span>
                                  </li>
                                  <li className="flex items-center justify-between">
                                    <span className="flex items-center text-textLight">
                                      <BiSolidParty className="mr-2 text-accent" /> Status
                                    </span>
                                    <span className="font-semibold capitalize">{yacht.status || '-'}</span>
                                  </li>
                                  {yacht.totalUnits && (
                                    <li className="flex items-center justify-between">
                                      <span className="flex items-center text-textLight">
                                        <IoCalendarNumber className="mr-2 text-accent" /> Total Units
                                      </span>
                                      <span className="font-semibold">{yacht.totalUnits}</span>
                                    </li>
                                  )}
                                  <li className="flex items-center justify-between">
                                    <span className="flex items-center text-textLight">
                                      <FaShip className="mr-2 text-accent" /> ID
                                    </span>
                                    <span className="font-semibold text-sm text-textLight">{yacht.stringId || yacht._id}</span>
                                  </li>
                                </ul>
                              </div>
                            </div>
                          </motion.div>
                        </motion.div>
                      )}

                      {/* Pricing Tab */}
                      {activeTab === 'pricing' && (
                        <motion.div 
                          key="pricing"
                          initial="hidden"
                          animate="visible"
                          exit="hidden"
                          variants={containerVariants}
                          className="space-y-8"
                        >
                          <motion.div variants={itemVariants}>
                            <h2 className="text-2xl font-bold text-[#002B5B] mb-4 flex items-center">
                              <FaEuroSign className="mr-2 text-[#7FDBB6]" /> Pricing Options
                            </h2>
                            
                            <div className="bg-[#F5F3EF] p-4 rounded-lg mb-6">
                              <h3 className="text-lg font-medium mb-2 text-[#2E3A59]">Season Information</h3>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="flex items-start">
                                  <FaSun className="text-yellow-500 mt-1 mr-2" />
                                  <div>
                                    <p className="font-medium text-[#2E3A59]">High Season</p>
                                    <p className="text-sm text-[#7A8B99]">{seasons.high}</p>
                                  </div>
                                </div>
                                <div className="flex items-start">
                                  <FaSnowflake className="text-blue-400 mt-1 mr-2" />
                                  <div>
                                    <p className="font-medium text-[#2E3A59]">Low Season</p>
                                    <p className="text-sm text-[#7A8B99]">{seasons.low}</p>
                                  </div>
                                </div>
                              </div>
                            </div>                            <div className="mb-6">
                              <h3 className="text-lg font-medium mb-4 text-[#2E3A59]">Price Overview</h3>
                              {/* Private Tour Pricing Section */}
                              <div className="mb-8">
                                <h4 className="font-medium text-lg mb-2 text-[#002B5B]">Private Charter Pricing</h4>
                                
                                {/* Private Half Day */}
                                <div className="mb-4">
                                  <h5 className="font-medium mb-2 text-[#2E3A59] flex items-center">
                                    <FaClock className="mr-2" /> Private Half Day
                                  </h5>
                                  
                                  {/* Low Season */}
                                  <div className="mb-3 border rounded-md overflow-hidden">
                                    <div className="bg-blue-50 px-4 py-2 border-b flex items-center">
                                      <FaSnowflake className="text-blue-400 mr-2" />
                                      <span className="font-medium">Low Season</span>
                                    </div>
                                    
                                    {pricingByType.private.halfDay.lowSeason?.guestTiers?.length > 0 ? (
                                      <div className="bg-white p-4">
                                        <table className="w-full text-sm">
                                          <thead>
                                            <tr className="border-b">
                                              <th className="text-left py-2">Number of Guests</th>
                                              <th className="text-right py-2">Price</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {pricingByType.private.halfDay.lowSeason.guestTiers.map((tier, idx) => (
                                              <tr key={idx} className="border-b last:border-0 hover:bg-gray-50">
                                                <td className="py-2">{tier.guests} guests</td>
                                                <td className="text-right font-medium">{formatPrice(tier.rate)}</td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    ) : (
                                      <div className="bg-white p-4 text-center text-gray-500">
                                        No pricing available for this season
                                      </div>
                                    )}
                                  </div>
                                  
                                  {/* High Season */}
                                  <div className="mb-3 border rounded-md overflow-hidden">
                                    <div className="bg-yellow-50 px-4 py-2 border-b flex items-center">
                                      <FaSun className="text-yellow-500 mr-2" />
                                      <span className="font-medium">High Season</span>
                                    </div>
                                    
                                    {pricingByType.private.halfDay.highSeason?.guestTiers?.length > 0 ? (
                                      <div className="bg-white p-4">
                                        <table className="w-full text-sm">
                                          <thead>
                                            <tr className="border-b">
                                              <th className="text-left py-2">Number of Guests</th>
                                              <th className="text-right py-2">Price</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {pricingByType.private.halfDay.highSeason.guestTiers.map((tier, idx) => (
                                              <tr key={idx} className="border-b last:border-0 hover:bg-gray-50">
                                                <td className="py-2">{tier.guests} guests</td>
                                                <td className="text-right font-medium">{formatPrice(tier.rate)}</td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    ) : (
                                      <div className="bg-white p-4 text-center text-gray-500">
                                        No pricing available for this season
                                      </div>
                                    )}
                                  </div>
                                </div>
                                
                                {/* Private Full Day */}
                                <div className="mb-4">
                                  <h5 className="font-medium mb-2 text-[#2E3A59] flex items-center">
                                    <FaClock className="mr-2" /> Private Full Day
                                  </h5>
                                  
                                  {/* Low Season */}
                                  <div className="mb-3 border rounded-md overflow-hidden">
                                    <div className="bg-blue-50 px-4 py-2 border-b flex items-center">
                                      <FaSnowflake className="text-blue-400 mr-2" />
                                      <span className="font-medium">Low Season</span>
                                    </div>
                                    
                                    {pricingByType.private.fullDay.lowSeason?.guestTiers?.length > 0 ? (
                                      <div className="bg-white p-4">
                                        <table className="w-full text-sm">
                                          <thead>
                                            <tr className="border-b">
                                              <th className="text-left py-2">Number of Guests</th>
                                              <th className="text-right py-2">Price</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {pricingByType.private.fullDay.lowSeason.guestTiers.map((tier, idx) => (
                                              <tr key={idx} className="border-b last:border-0 hover:bg-gray-50">
                                                <td className="py-2">{tier.guests} guests</td>
                                                <td className="text-right font-medium">{formatPrice(tier.rate)}</td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    ) : (
                                      <div className="bg-white p-4 text-center text-gray-500">
                                        No pricing available for this season
                                      </div>
                                    )}
                                  </div>
                                  
                                  {/* High Season */}
                                  <div className="mb-3 border rounded-md overflow-hidden">
                                    <div className="bg-yellow-50 px-4 py-2 border-b flex items-center">
                                      <FaSun className="text-yellow-500 mr-2" />
                                      <span className="font-medium">High Season</span>
                                    </div>
                                    
                                    {pricingByType.private.fullDay.highSeason?.guestTiers?.length > 0 ? (
                                      <div className="bg-white p-4">
                                        <table className="w-full text-sm">
                                          <thead>
                                            <tr className="border-b">
                                              <th className="text-left py-2">Number of Guests</th>
                                              <th className="text-right py-2">Price</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {pricingByType.private.fullDay.highSeason.guestTiers.map((tier, idx) => (
                                              <tr key={idx} className="border-b last:border-0 hover:bg-gray-50">
                                                <td className="py-2">{tier.guests} guests</td>
                                                <td className="text-right font-medium">{formatPrice(tier.rate)}</td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    ) : (
                                      <div className="bg-white p-4 text-center text-gray-500">
                                        No pricing available for this season
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                              
                              {/* Semi-Private Tour Pricing Section */}
                              <div>
                                <h4 className="font-medium text-lg mb-2 text-[#002B5B]">Semi-Private Charter Pricing</h4>
                                
                                {/* Semi-Private Half Day */}
                                <div className="mb-4">
                                  <h5 className="font-medium mb-2 text-[#2E3A59] flex items-center">
                                    <FaClock className="mr-2" /> Semi-Private Half Day
                                  </h5>
                                  
                                  {/* Low Season */}
                                  <div className="mb-3 border rounded-md overflow-hidden">
                                    <div className="bg-blue-50 px-4 py-2 border-b flex items-center">
                                      <FaSnowflake className="text-blue-400 mr-2" />
                                      <span className="font-medium">Low Season</span>
                                    </div>
                                    
                                    {pricingByType.semiPrivate.halfDay.lowSeason?.perGuestRate ? (
                                      <div className="bg-white p-4 flex items-center justify-between">
                                        <span>Price per person:</span>
                                        <span className="font-medium">{formatPrice(pricingByType.semiPrivate.halfDay.lowSeason.perGuestRate)}</span>
                                      </div>
                                    ) : pricingByType.semiPrivate.halfDay.lowSeason?.guestTiers?.length > 0 ? (
                                      <div className="bg-white p-4">
                                        <table className="w-full text-sm">
                                          <thead>
                                            <tr className="border-b">
                                              <th className="text-left py-2">Number of Guests</th>
                                              <th className="text-right py-2">Price per Person</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {pricingByType.semiPrivate.halfDay.lowSeason.guestTiers.map((tier, idx) => (
                                              <tr key={idx} className="border-b last:border-0 hover:bg-gray-50">
                                                <td className="py-2">{tier.guests} guests</td>
                                                <td className="text-right font-medium">{formatPrice(tier.rate)}</td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    ) : (
                                      <div className="bg-white p-4 text-center text-gray-500">
                                        No pricing available for this season
                                      </div>
                                    )}
                                  </div>
                                  
                                  {/* High Season */}
                                  <div className="mb-3 border rounded-md overflow-hidden">
                                    <div className="bg-yellow-50 px-4 py-2 border-b flex items-center">
                                      <FaSun className="text-yellow-500 mr-2" />
                                      <span className="font-medium">High Season</span>
                                    </div>
                                    
                                    {pricingByType.semiPrivate.halfDay.highSeason?.perGuestRate ? (
                                      <div className="bg-white p-4 flex items-center justify-between">
                                        <span>Price per person:</span>
                                        <span className="font-medium">{formatPrice(pricingByType.semiPrivate.halfDay.highSeason.perGuestRate)}</span>
                                      </div>
                                    ) : pricingByType.semiPrivate.halfDay.highSeason?.guestTiers?.length > 0 ? (
                                      <div className="bg-white p-4">
                                        <table className="w-full text-sm">
                                          <thead>
                                            <tr className="border-b">
                                              <th className="text-left py-2">Number of Guests</th>
                                              <th className="text-right py-2">Price per Person</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {pricingByType.semiPrivate.halfDay.highSeason.guestTiers.map((tier, idx) => (
                                              <tr key={idx} className="border-b last:border-0 hover:bg-gray-50">
                                                <td className="py-2">{tier.guests} guests</td>
                                                <td className="text-right font-medium">{formatPrice(tier.rate)}</td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    ) : (
                                      <div className="bg-white p-4 text-center text-gray-500">
                                        No pricing available for this season
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </motion.div>

                          {/* Extra Services */}
                          {yacht.extraServices && yacht.extraServices.length > 0 && (
                            <motion.div variants={itemVariants}>
                              <h3 className="text-xl font-semibold text-[#002B5B] mb-4 flex items-center">
                                <BiDollar className="mr-2 text-accent" /> Extra Services
                              </h3>
                              <div className="bg-gray p-4 rounded-lg">
                                <ul className="space-y-3">
                                  {yacht.extraServices.map((service, index) => (
                                    <li key={index} className="flex items-start">
                                      <FaCheck className="text-green-500 mt-1 mr-2 flex-shrink-0" />
                                      <span>{service}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </motion.div>
                          )}
                        </motion.div>
                      )}

                      {/* Amenities Tab */}
                      {activeTab === 'amenities' && (
                        <motion.div 
                          key="amenities"
                          initial="hidden"
                          animate="visible"
                          exit="hidden"
                          variants={containerVariants}
                          className="space-y-8"
                        >
                          <motion.div variants={itemVariants}>
                            <h2 className="text-2xl font-bold text-[#002B5B] mb-4 flex items-center">
                              <FaSwimmer className="mr-2 text-accent" /> Amenities & Inclusions
                            </h2>
                            
                            {/* Included Items */}
                            {yacht.includedItems && yacht.includedItems.length > 0 && (
                              <div className="mb-6">
                                <h3 className="text-xl font-semibold text-[#002B5B] mb-4 flex items-center">
                                  <FaCheck className="mr-2 text-green-500" /> Included in Your Cruise
                                </h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                  {yacht.includedItems.map((item, index) => (
                                    <div key={index} className="flex items-start bg-gray p-3 rounded-lg">
                                      <FaCheck className="text-green-500 mt-1 mr-2 flex-shrink-0" />
                                      <span>{item}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Signature Meal Details */}
                            {yacht.signatureMealDetails && yacht.signatureMealDetails.length > 0 && (
                              <div className="mb-6">
                                <h3 className="text-xl font-semibold text-[#002B5B] mb-4 flex items-center">
                                  <FaUtensils className="mr-2 text-accent" /> Signature Meal Options
                                </h3>
                                <div className="bg-gray p-6 rounded-lg">
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {yacht.signatureMealDetails.map((item, index) => (
                                      <div key={index} className="flex items-start">
                                        <div className="p-2 rounded-full bg-accent/10 mr-3 flex-shrink-0">
                                          {index % 2 === 0 ? (
                                            <FaUtensils className="text-accent" />
                                          ) : (
                                            <FaWineGlassAlt className="text-accent" />
                                          )}
                                        </div>
                                        <span>{item}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            )}
                          </motion.div>
                        </motion.div>
                      )}

                      {/* Destinations Tab */}
                      {/* {activeTab === 'destinations' && (
                        <motion.div 
                          key="destinations"
                          initial="hidden"
                          animate="visible"
                          exit="hidden"
                          variants={containerVariants}
                          className="space-y-8"
                        >
                          <motion.div variants={itemVariants}>
                            <h2 className="text-2xl font-bold text-[#002B5B] mb-4 flex items-center">
                              <FaMapMarkerAlt className="mr-2 text-accent" /> Sailing Destinations
                            </h2>
                            
                            {yacht.destinations?.available && yacht.destinations.available.length > 0 ? (
                              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                                {yacht.destinations.available.map((destination, index) => (
                                  <div key={index} className="bg-gray p-4 rounded-lg flex items-center">
                                    <MdLocationOn className="text-accent text-xl mr-2" />
                                    <span>{destination}</span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="bg-gray p-6 rounded-lg text-center">
                                <FaCompass className="text-4xl text-gray-400 mx-auto mb-2" />
                                <p className="text-textLight">No specific destinations listed for this yacht.</p>
                                <p className="text-sm text-textLight mt-2">Please contact us for available sailing areas.</p>
                              </div>
                            )}
                          </motion.div>
                        </motion.div>
                      )} */}
                    </AnimatePresence>
                  </div>
                </div>

                {/* Right Column - Enhanced Booking Form */}
                <div className="lg:w-1/3 flex flex-col">
                  <div className="sticky top-4 flex flex-col">
                    {/* Enhanced Price Display - Ocean Wave Gradient */}
                    <div className="bg-gradient-to-br from-[#002B5B] via-[#004D7A] to-[#00AEEF] p-6 rounded-t-lg text-white shadow-md relative overflow-hidden">
                      {/* Subtle wave pattern overlay */}
                  <div className="flex items-baseline justify-between mb-2">
                    <div>
                      {!formData.bookingDate || !formData.cruiseType ? (
                        <>
                          {/* Show lowest price when no selection is made */}
                          {yacht.lowestPriceInfo ? (
                            <div className="flex flex-col">
                              <span className="text-xs text-white/70">Starting from</span>
                              <div className="flex items-baseline">
                                <span className="text-3xl font-bold">
                                  €{yacht.lowestPriceInfo.price.toLocaleString()}
                                </span>
                                <span className="text-sm text-white/80 ml-2">
                                  {yacht.lowestPriceInfo.type}
                                </span>
                              </div>
                            </div>
                          ) : (
                            <span className="text-3xl font-bold">Select dates</span>
                          )}
                        </>
                      ) : (
                        <>
                          {/* Show calculated price when selection is made */}
                          <div className="flex flex-col">
                            <span className="text-3xl font-bold">
                              €{calculatedPrice.toFixed(2)}
                            </span>
                            <div className="flex items-center text-sm text-white/80 mt-1">
                              <span className="mr-2">{formData.cruiseType}</span>
                              {/* Show season badge */}
                              {(() => {
                                // Determine season based on selected date
                                const month = formData.bookingDate.getMonth() + 1;
                                const isHighSeason = [6, 7, 8].includes(month); // June, July, August
                                const isLowSeason = [4, 5, 9, 10].includes(month); // April, May, Sept, Oct
                                
                                if (isHighSeason) {
                                  return <span className="px-2 py-0.5 rounded-full bg-yellow-500/30 text-white text-xs">High Season</span>;
                                } else if (isLowSeason) {
                                  return <span className="px-2 py-0.5 rounded-full bg-blue-400/30 text-white text-xs">Low Season</span>;
                                } else {
                                  return <span className="px-2 py-0.5 rounded-full bg-green-500/30 text-white text-xs">N/A Season</span>;
                                }
                              })()}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                    {yacht.status === 'available' && (
                      <span className="bg-green-500 text-white text-xs px-2 py-1 rounded-full uppercase">
                        Available
                      </span>
                    )}
                  </div>
                      </div>
                      
                      {/* Price breakdown section */}
                      {(calculatedPrice > 0 || couponStatus.isValid) && (
                        <motion.div 
                            initial={{opacity: 0, y: 10}} 
                            animate={{opacity: 1, y: 0}}
                            className="text-sm text-white/80"
                        >
                            {/* Show per guest price if applicable */}
                            {calculatedPrice > 0 && formData.numberOfGuests > 1 && (
                              <p className="text-sm text-white/80 bg-sky-800/50 p-2">
                              €{(calculatedPrice / formData.numberOfGuests).toFixed(2)} per guest</p>
                            )}
                            
                            {/* Show base price if discount applies */}
                            {(() => {
                                const basePrice = calculatedPrice + (couponStatus.isValid ? couponStatus.discount : 0);
                                if (basePrice > calculatedPrice) {
                                    return <p className="text-sm text-white/80 bg-sky-800/50 p-2">Base Price: €{basePrice.toFixed(2)}</p>;
                                }
                                return null;
                            })()}
                            
                            {/* Show discount if coupon is valid */}
                            {couponStatus.isValid && (
                                <p className="text-green-300 flex items-center text-sm  bg-sky-800/50 p-2">
                                  <FaCheck className="mr-1" /> Discount: -€{couponStatus.discount.toFixed(2)}
                                </p>
                            )}
                        </motion.div>
                      )}
                      
                      {/* Partner commission */}
                      {isPartner && partnerCommission > 0 && calculatedPrice > 0 && (
                        <div className="text-sm text-green-300 pt-2 border-t border-white/20 mt-2">
                          <span>Est. Commission:</span>
                          <span> €{( (calculatedPrice / 1.24) * partnerCommission / 100).toFixed(2)}</span>
                        </div>
                      )}
                    </div>

                    {/* Price guidance message - NEW */}
                    {!formData.bookingDate || !formData.cruiseType ? (
                      <div className="bg-blue-50 p-3 border-b border-blue-100 text-sm text-blue-700">
                        <p className="flex items-center">
                          <FaInfoCircle className="mr-2 text-blue-500" />
                          Select your date and cruise type to see the exact price
                        </p>
                      </div>
                    ) : null}

                    {/* Booking Form */}
                    <div className="bg-gradient-to-tr to-blue-400 via-sky-500 from-blue-600 p-6 rounded-b-lg shadow-md flex-1">
                      <form onSubmit={handleSubmit} className="space-y-4">
                        {errors.general && (
                          <p className="text-red-600 text-sm bg-red-100 p-3 rounded -mt-2 mb-4">
                            {errors.general}
                          </p>
                        )}
                        
                        {/* Date selection with season indicator */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                          Booking Date *
                          </label>
                          <div data-error={!!errors.bookingDate}>
                          <DatePicker
                            selected={formData.bookingDate}
                            onChange={(date) => setFormData({...formData, bookingDate: date})}
                            customInput={
                            <CustomDateInput
                              placeholder="Select Date"
                              isError={!!errors.bookingDate}
                            />
                            }
                            dateFormat="dd/MM/yyyy"
                            minDate={new Date()}
                            placeholderText="Select Date"
                            wrapperClassName="w-full"
                            // Highlight different seasons
                            dayClassName={date => {
                            const month = date.getMonth() + 1;
                            if ([6, 7, 8].includes(month)) return "bg-yellow-50 hover:bg-yellow-100"; // High season
                            if ([4, 5, 9, 10].includes(month)) return "bg-blue-50 hover:bg-blue-100"; // Low season
                            return "bg-green-50 hover:bg-green-100"; // N/A season
                            }}
                          />
                          </div>
                          {errors.bookingDate && (
                          <p className="text-red-500 text-xs mt-1">{errors.bookingDate}</p>
                          )}
                          
                          {/* Season legend - NEW */}
                          <div className="flex justify-start gap-6 mt-1.5 text-xs text-gray-500">
                          <div className="flex justify-center items-center">
                            <span className="w-2 h-2 rounded-full bg-yellow-400 inline-block mr-1"></span>
                            High Season
                          </div>
                          {/* <div className="flex items-center">
                            <span className="w-2 h-2 rounded-full bg-blue-400 inline-block mr-1"></span>
                            Medium Season
                          </div> */}
                          <div className="flex items-center">
                            <span className="w-2 h-2 rounded-full bg-blue-700 border border-blue-800 inline-block mr-1"></span>
                            Low Season
                          </div>
                          </div>
                        </div>


                        {/* Cruise type selector with price indicators */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Cruise Type *
                          </label>
                          <div data-error={!!errors.cruiseType}>
                            <select
                              value={formData.cruiseType}
                              onChange={(e) => {
                                // Immediately update price on selection
                                setFormData({...formData, cruiseType: e.target.value});
                                
                                // Update cruise duration and destination selection based on cruise type
                                const newDuration = e.target.value.toLowerCase().includes('half') ? "half" : "full";
                                setCruiseDuration(newDuration);
                                
                                // If full day selected, check all destinations
                                if (newDuration === "full") {
                                  setDestinations({
                                    delos: true,
                                    rhenia: true,
                                    mykonos: true
                                  });
                                } else {
                                  // If half day, reset destinations
                                  setDestinations({
                                    delos: false,
                                    rhenia: false,
                                    mykonos: false
                                  });
                                }
                                
                                console.log("Selected cruise type:", e.target.value);
                              }}
                              className={`w-full h-[42px] px-3 border rounded bg-white appearance-none hover:border-blue-500 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.cruiseType ? 'border-red-500' : 'border-gray-200'}`}
                            >
                              <option value="">Select Cruise Type</option>                                {yacht.cruiseOptions?.map((option) => {
                                // Find price range for this cruise type to display
                                let priceInfo = "";
                                const optionType = option.type;
                                console.log("Option type:", optionType);
                                
                                // Helper function to get price from guest tiers
                                const getLowestTierPrice = (tiers) => {
                                  if (tiers && tiers.length > 0) {
                                    return Math.min(...tiers.map(tier => tier.rate));
                                  }
                                  return null;
                                };
                                
                                // Fix the pricing lookup logic - properly match private and semi-private options
                                if (optionType.toLowerCase().includes("private") && !optionType.toLowerCase().includes("semi")) {
                                  // Private options
                                  if (optionType.toLowerCase().includes("half")) {
                                    // Check for guest tiers first
                                    const lowSeasonTiers = pricingByType.private.halfDay.lowSeason?.guestTiers;
                                    const highSeasonTiers = pricingByType.private.halfDay.highSeason?.guestTiers;
                                    
                                    const lowPrice = getLowestTierPrice(lowSeasonTiers);
                                    const highPrice = getLowestTierPrice(highSeasonTiers);
                                    
                                    if (lowPrice || highPrice) {
                                      const lowestPrice = Math.min(...[lowPrice, highPrice].filter(Boolean));
                                      priceInfo = ` - from €${lowestPrice}`;
                                    }
                                  } 
                                  else if (optionType.toLowerCase().includes("full")) {
                                    // Check for guest tiers first
                                    const lowSeasonTiers = pricingByType.private.fullDay.lowSeason?.guestTiers;
                                    const highSeasonTiers = pricingByType.private.fullDay.highSeason?.guestTiers;
                                    
                                    const lowPrice = getLowestTierPrice(lowSeasonTiers);
                                    const highPrice = getLowestTierPrice(highSeasonTiers);
                                    
                                    if (lowPrice || highPrice) {
                                      const lowestPrice = Math.min(...[lowPrice, highPrice].filter(Boolean));
                                      priceInfo = ` - from €${lowestPrice}`;
                                    }
                                  }
                                }
                                else if (optionType.toLowerCase().includes("semi")) {
                                  // Semi-Private options
                                  if (optionType.toLowerCase().includes("half")) {
                                    // Check for per-guest rate first
                                    const lowSeasonRate = pricingByType.semiPrivate.halfDay.lowSeason?.perGuestRate;
                                    const highSeasonRate = pricingByType.semiPrivate.halfDay.highSeason?.perGuestRate;
                                    
                                    if (lowSeasonRate || highSeasonRate) {
                                      const lowestRate = Math.min(...[lowSeasonRate, highSeasonRate].filter(Boolean));
                                      priceInfo = ` - from €${lowestRate}/person`;
                                    }
                                    // Then check for guest tiers
                                    else {
                                      const lowSeasonTiers = pricingByType.semiPrivate.halfDay.lowSeason?.guestTiers;
                                      const highSeasonTiers = pricingByType.semiPrivate.halfDay.highSeason?.guestTiers;
                                      
                                      const lowPrice = getLowestTierPrice(lowSeasonTiers);
                                      const highPrice = getLowestTierPrice(highSeasonTiers);
                                      
                                      if (lowPrice || highPrice) {
                                        const lowestPrice = Math.min(...[lowPrice, highPrice].filter(Boolean));
                                        priceInfo = ` - from €${lowestPrice}/person`;
                                      }
                                    }
                                  }
                                  else if (optionType.toLowerCase().includes("full")) {
                                    // Check for per-guest rate first
                                    const lowSeasonRate = pricingByType.semiPrivate.fullDay.lowSeason?.perGuestRate;
                                    const highSeasonRate = pricingByType.semiPrivate.fullDay.highSeason?.perGuestRate;
                                    
                                    if (lowSeasonRate || highSeasonRate) {
                                      const lowestRate = Math.min(...[lowSeasonRate, highSeasonRate].filter(Boolean));
                                      priceInfo = ` - from €${lowestRate}/person`;
                                    }
                                    // Then check for guest tiers
                                    else {
                                      const lowSeasonTiers = pricingByType.semiPrivate.fullDay.lowSeason?.guestTiers;
                                      const highSeasonTiers = pricingByType.semiPrivate.fullDay.highSeason?.guestTiers;
                                      
                                      const lowPrice = getLowestTierPrice(lowSeasonTiers);
                                      const highPrice = getLowestTierPrice(highSeasonTiers);
                                      
                                      if (lowPrice || highPrice) {
                                        const lowestPrice = Math.min(...[lowPrice, highPrice].filter(Boolean));
                                        priceInfo = ` - from €${lowestPrice}/person`;
                                      }
                                    }
                                  }
                                }
                                
                                // Skip showing semi-private full day option
                                if (optionType.toLowerCase().includes("semi") && optionType.toLowerCase().includes("full")) {
                                  return null;
                                }
                                
                                return (
                                  <option key={option.type} value={option.type}>
                                  {option.type} ({option.durationHours || 'N/A'}hrs{priceInfo})
                                  </option>
                                );
                                })}
                              </select>
                              </div>
                              {errors.cruiseType && (
                              <p className="text-red-500 text-xs mt-1">{errors.cruiseType}</p>
                              )}
                            </div>

                                                            {/* Booking time selector */}
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Booking Time *
                                  </label>
                                  <div data-error={!!errors.bookingTime}>
                                  <select
                                    value={formData.bookingTime}
                                    onChange={(e) => setFormData({...formData, bookingTime: e.target.value})}
                                    className={`w-full h-[42px] px-3 border rounded bg-white appearance-none hover:border-blue-500 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                    errors.bookingTime ? 'border-red-500' : 'border-gray-200'
                                    }`}
                                    disabled={!formData.cruiseType}
                                  >
                                    <option value="">Select a time</option>
                                    
                                    {/* Morning option for half-day cruises */}
                                    {formData.cruiseType && formData.cruiseType.toLowerCase().includes('half') && (
                                    <option value="Morning: 10:00 - 15:00">Morning: 10:00 - 15:00</option>
                                    )}
                                    
                                    {/* Sunset option for half-day cruises */}
                                    {formData.cruiseType && formData.cruiseType.toLowerCase().includes('half') && (
                                    <option value="Sunset: 15:30 - 20:30">Sunset: 15:30 - 20:30</option>
                                    )}
                                    
                                    {/* Full day cruise options */}
                                    {formData.cruiseType && formData.cruiseType.toLowerCase().includes('full') && (
                                    <>
                                      <option value="10:00 - 18:00">10:00 - 18:00</option>
                                      <option value="11:00 - 19:00">11:00 - 19:00</option>
                                      <option value="12:00 - 20:00">12:00 - 20:00</option>
                                      <option value="13:00 - 21:00">13:00 - 21:00</option>
                                    </>
                                    )}
                                  </select>
                                  </div>
                                  {errors.bookingTime && (
                                  <p className="text-red-500 text-xs mt-1">{errors.bookingTime}</p>
                                  )}
                                  {!formData.cruiseType && (
                                  <p className="text-gray-500 text-xs mt-1">Please select a cruise type first</p>
                                  )}
                                  
                                  {/* Time slots may change notice for certain months */}
                                  {formData.bookingDate && [8, 9].includes(formData.bookingDate.getMonth()) && (
                                  <p className="text-amber-600 text-xs mt-1 italic">
                                    <FaInfoCircle className="inline mr-1" />
                                    Time slots may vary during September & October due to sunset times
                                  </p>
                                  )}
                                </div>

                                {/* Destinations selector */}
                                {formData.cruiseType && (
                                  <div className="space-y-3">
                                    <label className="block text-sm font-medium text-gray-700">
                                      Destinations *
                                      {formData.cruiseType.toLowerCase().includes('half') ? (
                                        <span className="text-xs text-gray-500 ml-2">(Please select one)</span>
                                      ) : (
                                        <span className="text-xs text-gray-500 ml-2">(All included)</span>
                                      )}
                                    </label>
                                    
                                    <div className="bg-white rounded-lg border border-gray-200 p-3">
                                      {/* Set cruise duration based on cruise type */}
                                      <input 
                                        type="hidden" 
                                        value={formData.cruiseType.toLowerCase().includes('half') ? "half" : "full"}
                                        onChange={(e) => handleDurationChange(e)}
                                      />
                                      
                                      <div className="space-y-2">
                                        <label className="flex items-start p-2 hover:bg-gray-50 rounded cursor-pointer">
                                          <input
                                            type="checkbox"
                                            name="delos"
                                            className="mt-1 mr-2"
                                            checked={destinations.delos}
                                            onChange={handleDestinationChange}
                                            disabled={formData.cruiseType.toLowerCase().includes('full')}
                                          />
                                          <div>
                                            <span className="font-medium text-gray-800 block">Delos Island</span>
                                            <span className="text-sm text-gray-600">Discover ancient ruins and mythological wonders on this UNESCO World Heritage site.</span>
                                          </div>
                                        </label>
                                        
                                        <label className="flex items-start p-2 hover:bg-gray-50 rounded cursor-pointer">
                                          <input
                                            type="checkbox"
                                            name="rhenia"
                                            className="mt-1 mr-2"
                                            checked={destinations.rhenia}
                                            onChange={handleDestinationChange}
                                            disabled={formData.cruiseType.toLowerCase().includes('full')}
                                          />
                                          <div>
                                            <span className="font-medium text-gray-800 block">Rhenia Island</span>
                                            <span className="text-sm text-gray-600">Escape to pristine beaches and crystal-clear waters, perfect for a relaxing getaway.</span>
                                          </div>
                                        </label>
                                        
                                        <label className="flex items-start p-2 hover:bg-gray-50 rounded cursor-pointer">
                                          <input
                                            type="checkbox"
                                            name="mykonos"
                                            className="mt-1 mr-2"
                                            checked={destinations.mykonos}
                                            onChange={handleDestinationChange}
                                            disabled={formData.cruiseType.toLowerCase().includes('full')}
                                          />
                                          <div>
                                            <span className="font-medium text-gray-800 block">Mykonos Island</span>
                                            <span className="text-sm text-gray-600">Uncover the beauty of Mykonos with its picturesque South Coastline and famous beaches.</span>
                                          </div>
                                        </label>
                                      </div>
                                      
                                      {/* Help text based on cruise type */}
                                      <p className="text-xs text-gray-500 mt-2 italic">
                                        {formData.cruiseType.toLowerCase().includes('half') ? (
                                          <span>Half-day cruises allow visiting one destination of your choice</span>
                                        ) : (
                                          <span>Full-day cruises include all destinations</span>
                                        )}
                                      </p>
                                    </div>
                                    
                                    {/* Make sure at least one destination is selected */}
                                    {formData.cruiseType.toLowerCase().includes('half') && 
                                      !destinations.delos && 
                                      !destinations.rhenia && 
                                      !destinations.mykonos && (
                                        <p className="text-amber-600 text-xs">
                                          <FaInfoCircle className="inline mr-1" /> 
                                          Please select one destination for your half-day cruise
                                        </p>
                                      )}
                                  </div>
                                )}

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Number of Guests * (Max: {yacht.guestCapacity})
                          </label>
                          <div data-error={!!errors.numberOfGuests}>
                            <CustomInput
                              type="number"
                              placeholder="Number of Guests"
                              value={formData.numberOfGuests}
                              onChange={(e) => {
                                const inputValue = parseInt(e.target.value, 10) || 1;
                                const cappedValue = Math.min(inputValue, yacht.guestCapacity);
                                setFormData({...formData, numberOfGuests: cappedValue});
                              }}
                              min="1"
                              max={yacht.guestCapacity}
                              className={errors.numberOfGuests ? "border-red-500" : ""}
                            />
                          </div>
                          {errors.numberOfGuests && (
                            <p className="text-red-500 text-xs mt-1">{errors.numberOfGuests}</p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            First Name *
                          </label>
                          <div data-error={!!errors.firstName}>
                            <CustomInput
                              type="text"
                              placeholder="First Name"
                              value={formData.firstName}
                              onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                              className={errors.firstName ? "border-red-500" : ""}
                            />
                          </div>
                          {errors.firstName && <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Last Name *
                          </label>
                          <div data-error={!!errors.lastName}>
                            <CustomInput
                              type="text"
                              placeholder="Last Name"
                              value={formData.lastName}
                              onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                              className={errors.lastName ? "border-red-500" : ""}
                            />
                          </div>
                          {errors.lastName && <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            E-Mail Address *
                          </label>
                          <div data-error={!!errors.email}>
                            <CustomInput
                              type="email"
                              placeholder="Email Address"
                              value={formData.email}
                              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                              className={errors.email ? "border-red-500" : ""}
                            />
                          </div>
                          {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Phone Number *
                          </label>
                          <div data-error={!!errors.phone}>
                            <CustomInput
                              type="tel"
                              placeholder="Phone Number"
                              value={formData.phone}
                              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                              className={errors.phone ? "border-red-500" : ""}
                            />
                          </div>
                          {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Additional Notes:
                          </label>
                          <textarea
                            value={formData.notes}
                            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                            rows="3"
                            placeholder="Any special requests or information?"
                            className="w-full p-3 border border-gray-200 rounded bg-white hover:border-blue-500 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Coupon Code
                          </label>
                          <div className="relative">
                            <input
                              type="text"
                              placeholder="Enter coupon code"
                              value={formData.couponCode}
                              onChange={(e) => {
                                const code = e.target.value.toUpperCase();
                                setFormData({ ...formData, couponCode: code });
                              }}
                              onBlur={() => validateCoupon(formData.couponCode)}
                              onKeyPress={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  validateCoupon(formData.couponCode);
                                }
                              }}
                              className={`w-full p-3 border rounded bg-white transition-colors duration-300 focus:outline-none focus:ring-2 ${
                                couponStatus.isValid
                                  ? "border-green-500 focus:ring-green-500"
                                  : couponStatus.message && formData.couponCode
                                  ? "border-red-500 focus:ring-red-500"
                                  : "border-gray-200 focus:ring-blue-500 hover:border-blue-500"
                              }`}
                            />
                            <button
                                type="button"
                                onClick={() => validateCoupon(formData.couponCode)}
                                className="absolute right-1 top-1 bottom-1 px-3 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                                disabled={!formData.couponCode}
                            >
                                Apply
                            </button>
                          </div>
                          {couponStatus.message && (
                              <div className={`text-sm mt-1 ${couponStatus.isValid ? "text-green-600" : "text-red-600"}`}>
                                {couponStatus.message}
                              </div>
                            )}
                        </div>

                        <hr className="my-4" />

                        {/* Price summary */}
                        <div className="space-y-1">
                          {(() => {
                                const basePrice = calculatedPrice + (couponStatus.isValid ? couponStatus.discount : 0);
                                if (basePrice > 0 && basePrice > calculatedPrice) {
                                    return (
                                        <div className="flex justify-between items-center text-sm text-gray-600">
                                            <span>Base Price:</span>
                                            <span>€{basePrice.toFixed(2)}</span>
                                        </div>
                                    );
                                }
                                return null;
                            })()}
                            {couponStatus.isValid && (
                                <div className="flex justify-between items-center text-sm text-green-600">
                                    <span>Coupon Discount:</span>
                                    <span>-€{couponStatus.discount.toFixed(2)}</span>
                                </div>
                            )}
                            {isPartner && partnerCommission > 0 && calculatedPrice > 0 && (
                                <div className="flex justify-between items-center text-sm text-green-600">
                                  <span>Est. Commission:</span>
                                  <span>€{( (calculatedPrice / 1.24) * partnerCommission / 100).toFixed(2)}</span>
                                </div>
                              )}
                            <div className="flex justify-between items-center font-bold text-lg mt-2 pt-2 border-t border-gray-200">
                                <span>Total Price:</span>
                                <span>€{calculatedPrice.toFixed(2)}</span>
                            </div>
                        </div>

                        <button
                          type="submit"
                          disabled={isSubmitting || calculatedPrice <= 0}
                          className={`w-full py-3 rounded font-semibold text-lg transition-colors duration-300 ${
                            isSubmitting || calculatedPrice <= 0
                              ? "bg-gray-400 cursor-not-allowed"
                              : "bg-blue-900 hover:bg-blue-600 text-white"
                          }`}
                        >
                          {isSubmitting ? "Processing..." : "Request Booking"}
                        </button>
                      </form>
                    </div>
                </div>
            </div>
        </main>

        <Footer />
      </div>

      {/* Photo Gallery Modal */}
      <AnimatePresence>
        {showGalleryModal && yacht.images && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center"
          >
            <div className="relative w-full h-full flex flex-col">
              {/* Close button */}
              <button 
                onClick={closeGallery}
                className="absolute top-4 right-4 z-10 text-white p-2 rounded-full bg-black/50 hover:bg-black/70"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              {/* Image counter */}
              <div className="absolute top-4 left-4 z-10 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
                {activeImageIndex + 1} / {yacht.images.length}
              </div>

              {/* Main image */}
              <div className="flex-1 flex items-center justify-center p-4">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={activeImageIndex}
                    src={`${'https://yacht-server.create-site.gr'}${yacht.images[activeImageIndex]}`}
                    alt={`${yacht.name} - View ${activeImageIndex + 1}`}
                    className="max-h-[80vh] max-w-full object-contain"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  />
                </AnimatePresence>
              </div>

              {/* Thumbnail navigation */}
              <div className="p-4 bg-black/70">
                <div className="flex overflow-x-auto space-x-2 px-4">
                  {yacht.images.map((img, index) => (
                    <div
                      key={index}
                      onClick={() => changeImage(index)}
                      className={`flex-shrink-0 w-20 h-20 cursor-pointer transition-all ${
                        index === activeImageIndex ? 'border-2 border-white' : 'opacity-60'
                      }`}
                    >
                      <img
                        src={`${'https://yacht-server.create-site.gr'}${img}`}
                        alt={`Thumbnail ${index + 1}`}
                        className="h-full w-full object-cover"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Navigation arrows */}
              <button 
                onClick={() => changeImage(activeImageIndex === 0 ? yacht.images.length - 1 : activeImageIndex - 1)}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 p-3 rounded-full bg-black/50 text-white hover:bg-black/70 transition-all"
                disabled={isAnimating}
              >
                <FaChevronLeft />
              </button>
              
              <button 
                onClick={() => changeImage(activeImageIndex === yacht.images.length - 1 ? 0 : activeImageIndex + 1)}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 p-3 rounded-full bg-black/50 text-white hover:bg-black/70 transition-all"
                disabled={isAnimating}
              >
                <FaChevronRight />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Other modals (existing) */}
      <ImportantInfoModal
        isOpen={showImportantInfo}
        onClose={() => setShowImportantInfo(false)}
      />

      <SuccessModal
        isOpen={showSuccess}
        onClose={() => {
            setShowSuccess(false);
        }}
        message="Your yacht booking request has been received! We will contact you shortly to confirm the details."
      />
    </>
  );
};

export default YachtDetails;
